import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
    House, 
    Article, 
    Package, 
    SignOut, 
    PencilSimple, 
    Trash, 
    Plus,
    FloppyDisk,
    X,
    Warning,
    Check,
    Key
} from '@phosphor-icons/react';
import { useAuth, api } from '../../contexts/AuthContext';

const AdminDashboard = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('content');
    const [content, setContent] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Edit modal state
    const [editingContent, setEditingContent] = useState(null);
    const [editingProduct, setEditingProduct] = useState(null);
    const [isNewProduct, setIsNewProduct] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [contentRes, productsRes] = await Promise.all([
                api.get('/api/content'),
                api.get('/api/products')
            ]);
            setContent(contentRes.data);
            setProducts(productsRes.data);
        } catch (err) {
            setError('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const handleSaveContent = async (key, data) => {
        try {
            await api.put(`/api/admin/content/${key}`, data);
            setSuccess('Content saved successfully');
            setEditingContent(null);
            fetchData();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to save content');
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleSaveProduct = async (product) => {
        try {
            if (isNewProduct) {
                await api.post('/api/admin/products', product);
                setSuccess('Product created successfully');
            } else {
                await api.put(`/api/admin/products/${product.id}`, product);
                setSuccess('Product updated successfully');
            }
            setEditingProduct(null);
            setIsNewProduct(false);
            fetchData();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to save product');
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleDeleteProduct = async (id) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;
        try {
            await api.delete(`/api/admin/products/${id}`);
            setSuccess('Product deleted successfully');
            fetchData();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to delete product');
            setTimeout(() => setError(''), 3000);
        }
    };

    const contentLabels = {
        'hero_title': 'Hero Title',
        'hero_subtitle': 'Hero Subtitle',
        'about_intro': 'About Introduction',
        'about_software': 'Software Division Description',
        'company_mission': 'Company Mission'
    };

    return (
        <div className="min-h-screen bg-background-base" data-testid="admin-dashboard">
            {/* Top Navigation */}
            <nav className="bg-background-surface border-b border-border-subtle px-4 py-3">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <img 
                            src="https://customer-assets.emergentagent.com/job_eng-software-hub/artifacts/3icn5ytz_Spindrift%20Engineering%20Software.png"
                            alt="Spindrift Engineering"
                            className="h-8 w-auto"
                        />
                        <span className="text-text-primary font-medium">Admin Dashboard</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link 
                            to="/"
                            className="text-text-muted hover:text-text-primary text-sm flex items-center gap-2"
                            data-testid="view-site-link"
                        >
                            <House size={18} />
                            View Site
                        </Link>
                        <span className="text-text-muted text-sm">
                            {user?.email}
                        </span>
                        <button
                            onClick={() => setShowPasswordModal(true)}
                            className="text-text-muted hover:text-text-primary text-sm flex items-center gap-2"
                            data-testid="change-password-button"
                        >
                            <Key size={18} />
                            Change Password
                        </button>
                        <button
                            onClick={handleLogout}
                            className="text-text-muted hover:text-text-primary text-sm flex items-center gap-2"
                            data-testid="logout-button"
                        >
                            <SignOut size={18} />
                            Logout
                        </button>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Alerts */}
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-sm text-red-400"
                        data-testid="error-alert"
                    >
                        <Warning size={20} />
                        {error}
                    </motion.div>
                )}
                {success && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/30 rounded-sm text-green-400"
                        data-testid="success-alert"
                    >
                        <Check size={20} />
                        {success}
                    </motion.div>
                )}

                {/* Tabs */}
                <div className="flex gap-4 mb-8 border-b border-border-subtle">
                    <button
                        onClick={() => setActiveTab('content')}
                        className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
                            activeTab === 'content' 
                                ? 'border-primary text-text-primary' 
                                : 'border-transparent text-text-muted hover:text-text-secondary'
                        }`}
                        data-testid="tab-content"
                    >
                        <Article size={18} />
                        Site Content
                    </button>
                    <button
                        onClick={() => setActiveTab('products')}
                        className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
                            activeTab === 'products' 
                                ? 'border-primary text-text-primary' 
                                : 'border-transparent text-text-muted hover:text-text-secondary'
                        }`}
                        data-testid="tab-products"
                    >
                        <Package size={18} />
                        Software Products
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-12 text-text-muted">Loading...</div>
                ) : (
                    <>
                        {/* Content Tab */}
                        {activeTab === 'content' && (
                            <div className="space-y-4" data-testid="content-section">
                                <h2 className="text-xl font-medium text-text-primary mb-6">
                                    Manage Website Content
                                </h2>
                                {Object.entries(contentLabels).map(([key, label]) => {
                                    const item = content.find(c => c.key === key);
                                    return (
                                        <div 
                                            key={key}
                                            className="bg-background-surface border border-border-subtle rounded-sm p-4"
                                            data-testid={`content-item-${key}`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <h3 className="text-sm font-medium text-text-primary">
                                                    {label}
                                                </h3>
                                                <button
                                                    onClick={() => setEditingContent({ key, ...item })}
                                                    className="text-primary hover:text-primary-hover flex items-center gap-1 text-sm"
                                                    data-testid={`edit-content-${key}`}
                                                >
                                                    <PencilSimple size={16} />
                                                    Edit
                                                </button>
                                            </div>
                                            <p className="text-text-secondary text-sm line-clamp-2">
                                                {item?.content || 'No content set'}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Products Tab */}
                        {activeTab === 'products' && (
                            <div data-testid="products-section">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-medium text-text-primary">
                                        Software Products
                                    </h2>
                                    <button
                                        onClick={() => {
                                            setIsNewProduct(true);
                                            setEditingProduct({
                                                name: '',
                                                description: '',
                                                url: '',
                                                status: 'coming_soon',
                                                order: products.length
                                            });
                                        }}
                                        className="btn-primary px-4 py-2 rounded-sm text-sm flex items-center gap-2"
                                        data-testid="add-product-button"
                                    >
                                        <Plus size={18} />
                                        Add Product
                                    </button>
                                </div>

                                {products.length === 0 ? (
                                    <div className="text-center py-12 bg-background-surface border border-border-subtle rounded-sm">
                                        <Package size={48} className="mx-auto text-text-muted mb-4" />
                                        <p className="text-text-muted">No products added yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {products.map((product) => (
                                            <div 
                                                key={product.id}
                                                className="bg-background-surface border border-border-subtle rounded-sm p-4"
                                                data-testid={`product-item-${product.id}`}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <h3 className="text-text-primary font-medium">
                                                                {product.name}
                                                            </h3>
                                                            <span className={`px-2 py-0.5 text-xs rounded ${
                                                                product.status === 'active' 
                                                                    ? 'bg-green-500/20 text-green-400' 
                                                                    : 'bg-secondary/20 text-secondary'
                                                            }`}>
                                                                {product.status === 'active' ? 'Live' : 'Coming Soon'}
                                                            </span>
                                                        </div>
                                                        <p className="text-text-secondary text-sm mb-2">
                                                            {product.description}
                                                        </p>
                                                        <p className="text-text-muted text-xs font-mono">
                                                            {product.url}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => {
                                                                setIsNewProduct(false);
                                                                setEditingProduct(product);
                                                            }}
                                                            className="p-2 text-text-muted hover:text-primary transition-colors"
                                                            data-testid={`edit-product-${product.id}`}
                                                        >
                                                            <PencilSimple size={18} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteProduct(product.id)}
                                                            className="p-2 text-text-muted hover:text-red-400 transition-colors"
                                                            data-testid={`delete-product-${product.id}`}
                                                        >
                                                            <Trash size={18} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Content Edit Modal */}
            {editingContent && (
                <ContentEditModal
                    content={editingContent}
                    onSave={handleSaveContent}
                    onClose={() => setEditingContent(null)}
                />
            )}

            {/* Product Edit Modal */}
            {editingProduct && (
                <ProductEditModal
                    product={editingProduct}
                    isNew={isNewProduct}
                    onSave={handleSaveProduct}
                    onClose={() => {
                        setEditingProduct(null);
                        setIsNewProduct(false);
                    }}
                />
            )}

            {/* Password Change Modal */}
            {showPasswordModal && (
                <PasswordChangeModal
                    onClose={() => setShowPasswordModal(false)}
                    onSuccess={(msg) => {
                        setSuccess(msg);
                        setShowPasswordModal(false);
                        setTimeout(() => setSuccess(''), 3000);
                    }}
                    onError={(msg) => {
                        setError(msg);
                        setTimeout(() => setError(''), 3000);
                    }}
                />
            )}
        </div>
    );
};

// Content Edit Modal Component
const ContentEditModal = ({ content, onSave, onClose }) => {
    const [title, setTitle] = useState(content.title || '');
    const [contentText, setContentText] = useState(content.content || '');
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        await onSave(content.key, { title, content: contentText });
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-background-surface border border-border-subtle rounded-sm w-full max-w-lg p-6"
                data-testid="content-edit-modal"
            >
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-medium text-text-primary">Edit Content</h3>
                    <button onClick={onClose} className="text-text-muted hover:text-text-primary">
                        <X size={20} />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm text-text-secondary mb-2">Title</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="form-input w-full px-4 py-2 rounded-sm text-text-primary"
                            data-testid="content-title-input"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-text-secondary mb-2">Content</label>
                        <textarea
                            value={contentText}
                            onChange={(e) => setContentText(e.target.value)}
                            rows={5}
                            className="form-input w-full px-4 py-2 rounded-sm text-text-primary resize-none"
                            data-testid="content-text-input"
                        />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn-secondary px-4 py-2 rounded-sm text-sm"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="btn-primary px-4 py-2 rounded-sm text-sm flex items-center gap-2"
                            data-testid="save-content-button"
                        >
                            <FloppyDisk size={18} />
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
};

// Product Edit Modal Component
const ProductEditModal = ({ product, isNew, onSave, onClose }) => {
    const [formData, setFormData] = useState({
        name: product.name || '',
        description: product.description || '',
        url: product.url || '',
        status: product.status || 'coming_soon',
        order: product.order || 0,
        id: product.id
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        await onSave(formData);
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-background-surface border border-border-subtle rounded-sm w-full max-w-lg p-6"
                data-testid="product-edit-modal"
            >
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-medium text-text-primary">
                        {isNew ? 'Add Product' : 'Edit Product'}
                    </h3>
                    <button onClick={onClose} className="text-text-muted hover:text-text-primary">
                        <X size={20} />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm text-text-secondary mb-2">Product Name</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            className="form-input w-full px-4 py-2 rounded-sm text-text-primary"
                            required
                            data-testid="product-name-input"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-text-secondary mb-2">Description</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                            rows={3}
                            className="form-input w-full px-4 py-2 rounded-sm text-text-primary resize-none"
                            required
                            data-testid="product-description-input"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-text-secondary mb-2">URL</label>
                        <input
                            type="text"
                            value={formData.url}
                            onChange={(e) => setFormData({...formData, url: e.target.value})}
                            className="form-input w-full px-4 py-2 rounded-sm text-text-primary"
                            placeholder="https://app.spindrift-eng.com"
                            required
                            data-testid="product-url-input"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-text-secondary mb-2">Status</label>
                        <select
                            value={formData.status}
                            onChange={(e) => setFormData({...formData, status: e.target.value})}
                            className="form-input w-full px-4 py-2 rounded-sm text-text-primary bg-background-base"
                            data-testid="product-status-select"
                        >
                            <option value="coming_soon">Coming Soon</option>
                            <option value="active">Active / Live</option>
                            <option value="deprecated">Deprecated</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm text-text-secondary mb-2">Display Order</label>
                        <input
                            type="number"
                            value={formData.order}
                            onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                            className="form-input w-full px-4 py-2 rounded-sm text-text-primary"
                            data-testid="product-order-input"
                        />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn-secondary px-4 py-2 rounded-sm text-sm"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="btn-primary px-4 py-2 rounded-sm text-sm flex items-center gap-2"
                            data-testid="save-product-button"
                        >
                            <FloppyDisk size={18} />
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
};

// Password Change Modal Component
const PasswordChangeModal = ({ onClose, onSuccess, onError }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (newPassword !== confirmPassword) {
            onError('New passwords do not match');
            return;
        }
        
        if (newPassword.length < 8) {
            onError('New password must be at least 8 characters');
            return;
        }

        setSaving(true);
        try {
            await api.post('/api/auth/change-password', {
                current_password: currentPassword,
                new_password: newPassword
            });
            onSuccess('Password changed successfully');
        } catch (err) {
            onError(err.response?.data?.detail || 'Failed to change password');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-background-surface border border-border-subtle rounded-sm w-full max-w-md p-6"
                data-testid="password-change-modal"
            >
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-medium text-text-primary flex items-center gap-2">
                        <Key size={20} className="text-secondary" />
                        Change Password
                    </h3>
                    <button onClick={onClose} className="text-text-muted hover:text-text-primary">
                        <X size={20} />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm text-text-secondary mb-2">Current Password</label>
                        <input
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className="form-input w-full px-4 py-2 rounded-sm text-text-primary"
                            required
                            autoComplete="current-password"
                            data-testid="current-password-input"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-text-secondary mb-2">New Password</label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="form-input w-full px-4 py-2 rounded-sm text-text-primary"
                            required
                            autoComplete="new-password"
                            placeholder="Minimum 8 characters"
                            data-testid="new-password-input"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-text-secondary mb-2">Confirm New Password</label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="form-input w-full px-4 py-2 rounded-sm text-text-primary"
                            required
                            autoComplete="new-password"
                            data-testid="confirm-password-input"
                        />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn-secondary px-4 py-2 rounded-sm text-sm"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="btn-primary px-4 py-2 rounded-sm text-sm flex items-center gap-2"
                            data-testid="save-password-button"
                        >
                            <FloppyDisk size={18} />
                            {saving ? 'Saving...' : 'Change Password'}
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
};

export default AdminDashboard;
